import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
// import { proc } from '@aexae/js_tools';

import socket from '../../utils/socket';

const initialState = {
  isLoading: false,
  error: false,
  treatedMissions: [],
  newMissions: [],
  numbers: {
    new: {
      unseen: 0,
      noResponse: 0,
      available: 0,
      unavailable: 0,
      thisWeek: 0,
      nextWeek: 0,
      thisMonth: 0
    }
  },
  numberNewMissions: 0,
  affectedMissions: [],
  selectedMission: {},
  // filter
  filters: {
    new: {
      noResponse: false,
      available: false,
      unavailable: false,
      thisWeek: false,
      nextWeek: false,
      thisMonth: false
    }
  }
};

const slice = createSlice({
  name: 'missions',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getMissions(state, action) {
      const { newMissions, affectedMissions, isReset } = action.payload;
      const lastDayOfWeek = moment().endOf('week').format('YYYY-MM-DD');
      const endOfNextWeek = moment().add(1, 'weeks').endOf('week').format('YYYY-MM-DD');
      const currentMonth = moment().format('YYYYMM');
      state.newMissions = newMissions;
      state.affectedMissions = affectedMissions;
      // on compte les missions sans réponse
      state.numberNewMissions = newMissions.filter((el) => el.response === 'Aucune').length;
      state.numbers.new.unseen = isReset ? 0 : newMissions.filter((el) => !el.viewed_at).length;
      state.numbers.new.noResponse = newMissions.filter((el) => el.response === 'Aucune').length;
      state.numbers.new.available = newMissions.filter((el) => el.response === 'Disponible').length;
      state.numbers.new.unavailable = newMissions.filter((el) => el.response === 'Pas disponible').length;
      state.numbers.new.thisWeek = newMissions.filter((el) => el.date_start <= lastDayOfWeek).length;
      state.numbers.new.nextWeek = newMissions.filter(
        (el) => el.date_start > lastDayOfWeek && el.date_start <= endOfNextWeek
      ).length;
      state.numbers.new.thisMonth = newMissions.filter((el) => el.month.toString() === currentMonth).length;
      // les filtres doivent suivre
      Object.keys(state.numbers.new).forEach((key) => {
        if (state.numbers.new[key] === 0 && state.filters.new[key]) {
          state.filters.new[key] = false;
        }
      });
      state.isLoading = false;
    },

    setSelectedMission(state, action) {
      state.selectedMission = action.payload;
    },

    updateMissionResponse(state, action) {
      const response = action.payload.mission?.response;
      const missions = state.newMissions;
      const lastDayOfWeek = moment().endOf('week').format('YYYY-MM-DD');
      const endOfNextWeek = moment().add(1, 'weeks').endOf('week').format('YYYY-MM-DD');
      const currentMonth = moment().format('YYYYMM');

      // on récupère l'index dans la liste des missions
      const idx = missions.findIndex((el) => el.mission_id === state.selectedMission.mission_id);

      // on remplace par les nouvelles données
      state.selectedMission.response = response;
      state.newMissions[idx] = state.selectedMission;

      // on compte les missions
      state.numberNewMissions = state.newMissions.filter((el) => el.response === 'Aucune').length;
      state.numbers.new.noResponse = state.newMissions.filter((el) => el.response === 'Aucune').length;
      state.numbers.new.available = state.newMissions.filter((el) => el.response === 'Disponible').length;
      state.numbers.new.unavailable = state.newMissions.filter((el) => el.response === 'Pas disponible').length;
      state.numbers.new.thisWeek = state.newMissions.filter((el) => el.date_start <= lastDayOfWeek).length;
      state.numbers.new.nextWeek = state.newMissions.filter(
        (el) => el.date_start > lastDayOfWeek && el.date_start <= endOfNextWeek
      ).length;
      state.numbers.new.thisMonth = state.newMissions.filter((el) => el.month.toString() === currentMonth).length;
      // les filtres doivent suivre l'update si plus d'options
      Object.keys(state.numbers.new).forEach((key) => {
        if (state.numbers.new[key] === 0 && state.filters.new[key]) {
          state.filters.new[key] = false;
        }
      });
    },

    updateNumberUnseen(state) {
      state.numbers.new.unseen = 0;
    },

    // FILTER
    filterMissions(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    updateNumbersInFilters(state, action) {
      state.numbers = { ...state.numbers, ...action.payload };
    }
  }
});

// Reducer
export default slice.reducer;

// actions
export const { setSelectedMission, filterMissions, updateNumbersInFilters } = slice.actions;

/**
 * Fetches missions from the server.
 *
 * @param {boolean} [isReset=false] - Whether to reset unseen mission count
 * @returns {Function} The thunk function
 */
export function getMissions(isReset = false) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/missions');
      dispatch(slice.actions.getMissions({ ...result, isReset }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Updates the viewed_at field for the specified missions.
 *
 * @param {Object} params - The parameters containing mission IDs
 * @param {Array} params.ids - The IDs of the missions to update
 * @returns {Function} The thunk function
 */
export function updateMissionViewedAt({ ids }) {
  return async (dispatch) => {
    try {
      await socket.put('/missions', { resourceMissionIds: ids, viewedAt: moment().format('YYYY-MM-DD HH:mm:ss') });
      dispatch(slice.actions.updateNumberUnseen());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Updates the response for a specified mission.
 *
 * @param {Object} props - The properties containing mission details
 * @param {number} props.id - The ID of the mission to update
 * @param {string} props.response - The new response for the mission
 * @param {Function} [props.callback] - The callback function to execute after updating the mission
 * @returns {Function} The thunk function
 */
export function updateMissionResponse(props) {
  const { id, response, callback } = props;
  const responseAt = moment().format('YYYY-MM-DD HH:mm:ss');
  return async (dispatch) => {
    try {
      const result = await socket.put('/missions', { resourceMissionId: id, response, responseAt });
      dispatch(slice.actions.updateMissionResponse(result));
      if (result.success) {
        let message = '';
        switch (response) {
          case 2:
            message = "Merci d'avoir répondu.";
            break;
          case 3:
            message = "Merci d'avoir répondu. Vous serez informé si vous êtes affecté sur cette mission";
            break;
          default:
            message = 'Réponse retirée';
            break;
        }
        callback({ message, variant: 'success' });
      }
      return result;
    } catch (error) {
      if (callback) {
        callback({ message: 'Une erreur est survenue, veuillez réessayer ultérieurement.', variant: 'error' });
      }
      dispatch(slice.actions.hasError(error));
    }
  };
}
